"use client";

import { useState, useEffect } from "react";
import PlaceCard from "@/components/places/PlaceCard";
import PlaceFilters from "@/components/places/PlaceFilters";
import PlaceLayout from "@/components/places/PlaceLayout";
import { fetchConcesiones } from "@/lib/api/datosGenerales/fetchConcesiones";
import { fetchCategoriaConcesiones } from "@/lib/api/datosGenerales/fetchCategoriaConcesiones";
import { Concesiones } from "@/lib/api/datosGenerales/types/concesiones";
import { useLocale } from "next-intl";
import { useTranslations } from "next-intl";
import ConcesionesLoadingSkeleton from "@/components/ConcesionesLoadingSkeleton";
import { type Locale } from "@/i18n/config";

export default function TiendasPage() {
  const t = useTranslations("searchBar");
  const [selectedTerminal, setSelectedTerminal] = useState("todas");
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [search, setSearch] = useState("");
  const [data, setData] = useState<Concesiones[]>([]);
  const [categories, setCategories] = useState<string[]>([]);
  const locale = useLocale() as Locale;
  const [loading, setLoading] = useState(true);
  const [visibleCount, setVisibleCount] = useState(9);

  useEffect(() => {
    const fetchData = async () => {
      const [concesiones, categoriasData] = await Promise.all([
        fetchConcesiones(locale, "tienda"),
        fetchCategoriaConcesiones(locale, "tienda"),
      ]);
      setData(concesiones);
      setCategories(categoriasData.map((cat) => cat.nombre));
      setLoading(false);
    };
    fetchData();
  }, [locale]);
  if (loading) {
    return <ConcesionesLoadingSkeleton />;
  }

  const filteredData = data.filter((item) => {
    const matchesTerminal =
      selectedTerminal === "todas" || item.terminal.includes(selectedTerminal);
    const matchesCategory =
      selectedCategories.length === 0 ||
      selectedCategories.some((cat) => item.categoria.includes(cat));
    // Búsqueda en nombre
    const matchesName = item.nombre
      .toLowerCase()
      .includes(search.toLowerCase());

    // Búsqueda en tags
    const matchesTags =
      item.tags?.some((tag) =>
        tag.nombre.toLowerCase().includes(search.toLowerCase()),
      ) || false;

    const matchesSearch = matchesName || matchesTags;

    return matchesTerminal && matchesCategory && matchesSearch;
  });

  const loadMore = () => {
    setVisibleCount((prevCount) => prevCount + 9);
  };

  return (
    <PlaceLayout imageSrc="https://galeria.tocumenaero.com/gallery/api/Panor%C3%A1micas%20internas/9M1A0049.jpg">
      <PlaceFilters
        title={t("tiendas")}
        selectedTerminal={selectedTerminal}
        setSelectedTerminal={setSelectedTerminal}
        selectedCategories={selectedCategories}
        setSelectedCategories={setSelectedCategories}
        search={search}
        setSearch={setSearch}
        categories={categories}
      />
      <div className="w-[80%] mx-auto my-10">
        <p className="text-sm text-gray-600 mb-4">
          {t("mostrando", {
            visible: Math.min(visibleCount, filteredData.length),
            total: filteredData.length,
          })}
        </p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {filteredData.length > 0 ? (
            filteredData
              .slice(0, visibleCount)
              .map((item) => (
                <PlaceCard
                  key={item.id}
                  id={item.id}
                  image={item.foto}
                  title={item.nombre}
                  description={item.categoria || ""}
                  terminales={[item.terminal || ""]}
                  link={item.mapaUrl}
                  tipo={item.tipoConcesion}
                  detalleUbicacion={item.detalleUbicacion}
                />
              ))
          ) : (
            <p className="col-span-3 text-center pt-10 text-gray-500 font-medium">
              {t("error")}
            </p>
          )}
        </div>
        {visibleCount < filteredData.length && (
          <div className="text-center mt-8">
            <button
              onClick={loadMore}
              className="bg-blue-900 text-white px-6 py-2 rounded-md hover:bg-blue-800 transition-colors"
            >
              {t("verMas")}
            </button>
          </div>
        )}
      </div>
    </PlaceLayout>
  );
}
